import { useState, useEffect } from 'react'
import {
  Video,
  Calendar,
  Clock,
  Users,
  ExternalLink,
  Search,
  ArrowRight,
  CheckCircle,
  Filter,
  ChevronRight,
  Sparkles,
  Play
} from 'lucide-react'

interface LiveSession {
  id: string
  title: string
  description: string
  scheduledAt: string
  duration: number
  meetingLink?: string
  meetingPasscode?: string
  instructor: string
  isActive: boolean
  isPaid: boolean
  price?: number
  maxParticipants?: number
  enrolledCount?: number
  status: 'upcoming' | 'live' | 'completed'
  isFull?: boolean
  isUnlimited?: boolean
  registrationDeadline?: string
  registrationClosed?: boolean
}

export default function UserLiveSessions() {
  const [sessions, setSessions] = useState<LiveSession[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'upcoming' | 'completed'>('upcoming')
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    fetchLiveSessions()
  }, [])

  const fetchLiveSessions = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/api/live-sessions', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        const formattedSessions = (data.sessions || []).map((session: any) => ({
          id: session.id || session._id,
          title: session.title,
          description: session.description,
          scheduledAt: session.scheduledAt,
          duration: session.duration,
          meetingLink: session.meetingLink,
          meetingPasscode: session.meetingPassword,
          instructor: session.instructor || 'Instructor',
          isActive: session.isActive,
          isPaid: session.isRegistered || false,
          price: session.cost || 0,
          maxParticipants: session.maxParticipants || 0,
          enrolledCount: session.registeredCount || session.registeredUsers?.length || 0,
          status: determineStatus(session.scheduledAt, session.duration),
          isFull: session.isFull || false,
          isUnlimited: session.isUnlimited || false,
          registrationDeadline: session.registrationDeadline,
          registrationClosed: session.registrationClosed || false
        }))

        const sorted = formattedSessions.sort((a: LiveSession, b: LiveSession) => {
          if (a.status === 'live' && b.status !== 'live') return -1
          if (b.status === 'live' && a.status !== 'live') return 1
          return new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime()
        })

        setSessions(sorted)
      }
    } catch (error) {
      console.error('Error fetching live sessions:', error)
    } finally {
      setLoading(false)
    }
  }

  const determineStatus = (scheduledAt: string, duration: number): 'upcoming' | 'live' | 'completed' => {
    const now = new Date()
    const sessionStart = new Date(scheduledAt)
    const sessionEnd = new Date(sessionStart.getTime() + duration * 60000)

    if (now < sessionStart) return 'upcoming'
    if (now >= sessionStart && now <= sessionEnd) return 'live'
    return 'completed'
  }

  const filteredSessions = sessions.filter(session => {
    const matchesTab = activeTab === 'upcoming'
      ? (session.status === 'upcoming' || session.status === 'live')
      : session.status === 'completed'

    const matchesSearch =
      searchQuery === '' ||
      session.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      session.description.toLowerCase().includes(searchQuery.toLowerCase())

    return matchesTab && matchesSearch
  })

  const handleEnrollNow = async (session: LiveSession) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const orderResponse = await fetch('/api/payments/create-live-session-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ liveSessionId: session.id, amount: session.price }),
      });

      if (!orderResponse.ok) throw new Error('Failed to create order');

      const orderData = await orderResponse.json();
      const userData = JSON.parse(localStorage.getItem('user') || '{}');

      const options = {
        key: import.meta.env.VITE_RAZORPAY_KEY_ID,
        amount: orderData.amount,
        currency: orderData.currency,
        name: "Sudha's Makeup Academy",
        description: `Enrollment for ${session.title}`,
        order_id: orderData.orderId,
        handler: async (response: any) => {
          try {
            const verificationResponse = await fetch('/api/payments/verify-live-session-payment', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
              body: JSON.stringify({
                razorpayPaymentId: response.razorpay_payment_id,
                razorpayOrderId: response.razorpay_order_id,
                razorpaySignature: response.razorpay_signature,
                liveSessionId: session.id,
              }),
            });

            if (!verificationResponse.ok) throw new Error('Payment verification failed');
            alert('Enrollment successful!');
            await fetchLiveSessions();
          } catch (error) {
            alert('Enrollment failed: ' + (error instanceof Error ? error.message : 'Please try again.'));
          }
        },
        prefill: { name: userData.name, email: userData.email },
        theme: { color: '#8A2BE2' },
      };

      const rzp = new (window as any).Razorpay(options);
      rzp.open();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Please try again.';
      if (errorMessage.includes('maximum participant')) alert('No space available for this class');
      else if (errorMessage.includes('Registration deadline')) alert('Registration deadline has passed');
      else alert('Enrollment failed. ' + errorMessage);
    }
  };

  const handleJoinSession = (session: LiveSession) => {
    if (session.meetingLink) window.open(session.meetingLink, '_blank')
    else alert('Meeting link not available yet')
  }

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="relative">
        <div className="w-12 h-12 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-2 h-2 bg-violet-600 rounded-full"></div>
        </div>
      </div>
    </div>
  )

  return (
    <div className="space-y-8">

      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
            Live <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-600 to-rose-600">Sessions</span>
          </h1>
          <p className="text-slate-600 dark:text-slate-400 max-w-xl">
            Join our interactive live classes to learn directly from experts, ask questions, and master new skills in real-time.
          </p>
        </div>

        {/* Search & Filter */}
        <div className="flex items-center gap-3 bg-white dark:bg-slate-800 p-1.5 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search sessions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-4 py-2 bg-transparent text-sm w-48 md:w-64 focus:outline-none text-slate-900 dark:text-white placeholder-slate-400"
            />
          </div>
          <div className="h-6 w-px bg-slate-200 dark:bg-slate-700"></div>
          <div className="flex bg-slate-100 dark:bg-slate-700/50 rounded-xl p-1">
            {(['upcoming', 'completed'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-1.5 text-xs font-semibold rounded-lg transition-all ${activeTab === tab
                    ? 'bg-white dark:bg-slate-600 text-violet-600 dark:text-violet-300 shadow-sm'
                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
                  }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Sessions Grid */}
      {filteredSessions.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredSessions.map((session) => (
            <div
              key={session.id}
              className={`group bg-white dark:bg-slate-800 rounded-3xl overflow-hidden border border-slate-200 dark:border-slate-700 hover:shadow-xl hover:shadow-violet-500/10 transition-all duration-300 flex flex-col ${session.status === 'live' ? 'ring-2 ring-rose-500 ring-offset-2 dark:ring-offset-slate-900' : ''
                }`}
            >
              {/* Card Header / Cover */}
              <div className="relative h-32 bg-gradient-to-br from-slate-900 to-slate-800 p-6 overflow-hidden">
                {/* Abstract Background Shapes */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-violet-500/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-rose-500/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>

                <div className="relative z-10 flex justify-between items-start">
                  <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl px-3 py-1.5 text-white">
                    <div className="text-xs font-medium opacity-80 uppercase tracking-wider">
                      {new Date(session.scheduledAt).toLocaleDateString('en-US', { month: 'short' })}
                    </div>
                    <div className="text-xl font-bold leading-none mt-0.5">
                      {new Date(session.scheduledAt).getDate()}
                    </div>
                  </div>

                  {session.status === 'live' ? (
                    <div className="flex items-center gap-1.5 bg-rose-500 text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider animate-pulse">
                      <span className="w-1.5 h-1.5 bg-white rounded-full"></span> Live
                    </div>
                  ) : (
                    <div className="bg-black/30 backdrop-blur-sm text-white/90 px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1.5">
                      <Clock className="w-3 h-3" />
                      {new Date(session.scheduledAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  )}
                </div>
              </div>

              {/* Card Content */}
              <div className="p-6 flex-1 flex flex-col">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-2.5 py-0.5 rounded-md bg-violet-50 dark:bg-violet-500/10 text-violet-600 dark:text-violet-300 text-xs font-semibold border border-violet-100 dark:border-violet-500/20">
                      {session.duration} mins
                    </span>
                    {session.isPaid ? (
                      <span className="px-2.5 py-0.5 rounded-md bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs font-semibold border border-emerald-100 dark:border-emerald-500/20">
                        Premium
                      </span>
                    ) : (
                      <span className="px-2.5 py-0.5 rounded-md bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 text-xs font-semibold border border-blue-100 dark:border-blue-500/20">
                        Free
                      </span>
                    )}
                  </div>

                  <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2 line-clamp-2 group-hover:text-violet-600 dark:group-hover:text-violet-400 transition-colors">
                    {session.title}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400 text-sm line-clamp-3 mb-4">
                    {session.description}
                  </p>
                </div>

                {/* Footer Info */}
                <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 border-t border-slate-100 dark:border-slate-700/50 pt-4 mt-2 mb-5">
                  <div className="flex items-center gap-1.5">
                    <Video className="w-3.5 h-3.5" />
                    <span className="font-medium">{session.instructor}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5" />
                    <span>{session.enrolledCount} enrolled</span>
                  </div>
                </div>

                {/* Action Button */}
                {session.status === 'live' && session.isPaid ? (
                  <button
                    onClick={() => handleJoinSession(session)}
                    className="w-full py-3 bg-rose-600 hover:bg-rose-700 text-white font-semibold rounded-xl transition-all shadow-lg shadow-rose-500/30 flex items-center justify-center gap-2 group-hover:scale-[1.02]"
                  >
                    <Play className="w-4 h-4 fill-current" /> Join Live Class
                  </button>
                ) : session.status === 'upcoming' && !session.isPaid ? (
                  <button
                    onClick={() => handleEnrollNow(session)}
                    className="w-full py-3 bg-slate-900 dark:bg-white hover:bg-slate-800 dark:hover:bg-slate-100 text-white dark:text-slate-900 font-semibold rounded-xl transition-all shadow-lg shadow-slate-500/20 flex items-center justify-center gap-2 group-hover:scale-[1.02]"
                  >
                    Register for {session.price ? `₹${session.price}` : 'Free'} <ArrowRight className="w-4 h-4" />
                  </button>
                ) : session.status === 'upcoming' && session.isPaid ? (
                  <div className="w-full py-3 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-semibold rounded-xl border border-emerald-100 dark:border-emerald-500/20 flex items-center justify-center gap-2">
                    <CheckCircle className="w-5 h-5" /> Registered
                  </div>
                ) : (
                  <button disabled className="w-full py-3 bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500 font-medium rounded-xl cursor-not-allowed">
                    Session Ended
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-20 px-4 bg-white dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-700 border-dashed">
          <div className="w-20 h-20 bg-violet-50 dark:bg-violet-900/20 rounded-full flex items-center justify-center mb-6">
            <Calendar className="w-10 h-10 text-violet-300 dark:text-violet-500" />
          </div>
          <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">No sessions found</h3>
          <p className="text-slate-500 dark:text-slate-400 text-center max-w-sm mb-8">
            {searchQuery
              ? `We couldn't find any sessions matching "${searchQuery}"`
              : `There are no ${activeTab} live sessions at the moment. Check back later!`}
          </p>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="text-violet-600 dark:text-violet-400 font-semibold hover:underline"
            >
              Clear search
            </button>
          )}
        </div>
      )}
    </div>
  )
}